import React, {useState, useEffect, useContext} from 'react';
import {authAPI} from '../../../Api/api';
import './ResetPassword.css'
import CustomModal from '../../../Component/UI/CustomModal/customModal';
import {FaEye, FaEyeSlash, FaLock} from "react-icons/fa";
import { ImCross } from "react-icons/im";
import {CheckCircle, Lock} from "lucide-react";
import {useTranslation} from "react-i18next";
import Button from '../../../Component/UI/Button/Button';
import LanguageContext from '../../../Context/LanguageContext';

const ResetPassword = ({navigate}) => {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const lang = i18n.language;

    const [email, setEmail] = useState('');
    const [token, setToken] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const passwordRequirements = [
        { id: 'length', label: t('resetPasswordPage.reqMinLength'), test: (pwd) => pwd.length >= 8 },
        { id: 'uppercase', label: t('resetPasswordPage.reqUppercase'), test: (pwd) => /[A-Z]/.test(pwd) },
        { id: 'lowercase', label: t('resetPasswordPage.reqLowercase'), test: (pwd) => /[a-z]/.test(pwd) },
        { id: 'number', label: t('resetPasswordPage.reqNumber'), test: (pwd) => /[0-9]/.test(pwd) },
    ];

    const validatePassword = (pwd) => {
        return passwordRequirements.every(req => req.test(pwd));
    };

    const handleSuccessModal = () => {
        setIsSuccessPopupOpen(false);
        navigate(`/${lang}/sign-in`);
    }

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const tokenParam = params.get('token');
        const emailParam = params.get('email');
        if (tokenParam) setToken(tokenParam);
        if (emailParam) setEmail(emailParam);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const newErrors = {};

        if (!token || !email || !password || !confirmPassword) {
            setError(t('resetPasswordPage.allFieldsRequired'));
            return;
        }

        if (!password) {
            newErrors.password = t('resetPasswordPage.passwordRequired');
        } else if (!validatePassword(password)) {
            newErrors.password = t('resetPasswordPage.passwordDoesNotMeetRequirements');
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = t('resetPasswordPage.confirmPasswordRequired');
        } else if (password !== confirmPassword) {
            newErrors.confirmPassword = t('resetPasswordPage.passwordsDoNotMatch');
        }

        if (password !== confirmPassword) {
            setError(t('resetPasswordPage.passwordsDoNotMatch'));
            return;
        }

        setLoading(true);
        try {
            const response = await authAPI.resetPassword({ token, email, password, confirmPassword });
            setMessage(response?.data?.message || t('resetPasswordPage.passwordResetSuccess'));
            setIsSuccessPopupOpen(true);
            setPassword('');
            setConfirmPassword('');
            setIsSubmitted(true);
        } catch (err) {
            const backendMessage = err?.response?.data?.message;
            setError(backendMessage || t('resetPasswordPage.unableToResetPassword'));
        } finally {
            setLoading(false);
        }
    };

    const reverseClass = dir() === 'rtl' ? 'reverse' : '';

    return (
        <main className='reset-password-main' lang={['fa', 'ps', 'ar'].includes(lang) ? lang : 'en'}>
            <section className='reset-password-section auth-public-section'>
                <CustomModal
                    isModalOpen={isSuccessPopupOpen}
                    setIsModalOpen={setIsSuccessPopupOpen}
                >
                    <h1 className='custom-modal-successful-title'>
                        {t('resetPasswordPage.changedTitle')}
                    </h1>
                    <p className='custom-modal-description'>
                        {message}
                    </p>
                    <Button
                        type="button"
                        onClick={handleSuccessModal}
                    >
                        {t('resetPasswordPage.ok')}
                    </Button>
                </CustomModal>
                <div className="container">

                    {!isSubmitted ? (
                        <>
                            <form className="loginForm" onSubmit={handleSubmit}>
                                <div className="reset-password-header">
                                    <div className="reset-password-icon-wrapper">
                                        <Lock size={32} className="reset-password-lock-icon" />
                                    </div>
                                    <h1 className="reset-password-title">{t('resetPasswordPage.setNewPasswordTitle')}</h1>
                                    <p className="reset-password-subtitle">
                                        {isSubmitted
                                            ? t('resetPasswordPage.passwordResetSuccessfully')
                                            : t('resetPasswordPage.enterNewPassword')}
                                    </p>
                                    <p>
                                        {email}
                                    </p>
                                </div>

                                <div className='reset-password-content'>
                                    <div className={`reset-password-box ${reverseClass}`}>
                                        <FaLock className='reset-password-icon'/>
                                        <div className="reset-password-input-wrapper">
                                            <input
                                                type={showNewPassword ? 'text' : 'password'}
                                                className="reset-password-input"
                                                placeholder=" "
                                                name="password"
                                                value={password}
                                                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                                                required
                                            />
                                            <label className="reset-password-label">
                                                {t('resetPasswordPage.passwordLabel')}
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                aria-label={showNewPassword ? t('resetPasswordPage.hidePassword') : t('resetPasswordPage.showPassword')}
                                                className="reset-password-toggle"
                                            >
                                                {showNewPassword ? <FaEyeSlash className='reset-password-eye-icon'/> : <FaEye className='reset-password-eye-icon'/>}
                                            </button>
                                        </div>
                                    </div>

                                    <div className={`reset-password-box ${reverseClass}`}>
                                        <FaLock className='reset-password-icon'/>
                                        <div className="reset-password-input-wrapper">
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                className="reset-password-input"
                                                placeholder=" "
                                                name="password"
                                                value={confirmPassword}
                                                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                                                required
                                            />
                                            <label className="reset-password-label">
                                                {t('resetPasswordPage.confirmPasswordLabel')}
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                aria-label={showConfirmPassword ? t('resetPasswordPage.hidePassword') : t('resetPasswordPage.showPassword')}
                                                className="reset-password-toggle"
                                            >
                                                {showConfirmPassword ? <FaEyeSlash className='reset-password-eye-icon'/> : <FaEye className='reset-password-eye-icon'/>}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Password Requirements */}
                                {password && (
                                    <div className="new-password-requirements">
                                        <p className="new-password-requirements-title">{t('resetPasswordPage.requirementsTitle')}</p>
                                        <ul className="new-password-requirements-list">
                                            {passwordRequirements.map((req) => {
                                                const isMet = req.test(password);
                                                return (
                                                    <li
                                                        key={req.id}
                                                        className={`new-password-requirement ${isMet ? 'new-password-requirement-met' : ''}`}
                                                    >
                                                        {isMet ? (
                                                            <CheckCircle size={16} className="new-password-requirement-icon-success" />
                                                        ) : (
                                                            <ImCross size={16} className="new-password-requirement-icon-error" />
                                                        )}
                                                        {req.label}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}

                                <div>
                                    <Button className="reset-password-button" type="submit" disabled={loading}>
                                        {loading ? t('resetPasswordPage.changingPassword') : t('resetPasswordPage.changePassword')}
                                    </Button>
                                </div>

                                {error && <p className="auth-form-error" role="alert">{error}</p>}
                            </form>
                        </>
                    ) : (
                        <div className="new-password-success-container">
                            <div className="new-password-success-card">
                                <CheckCircle size={48} className="new-password-success-icon" />
                                <p className="new-password-success-text">
                                    {t('resetPasswordPage.useNewPassword')}
                                </p>
                            </div>
                            <Button
                                type="button"
                                onClick={() => navigate(`/${lang}/sign-in`)}
                                className="reset-password-button"
                            >
                                {t('resetPasswordPage.goToLogin')}
                            </Button>
                        </div>
                    )}

                </div>

            </section>
        </main>
    );
};

export default ResetPassword;
